import React, { useEffect, useState } from "react";
import api from "../../config/axiosConfig";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import "./Rapport.css";
import { useTenantBranding } from "../hooks/useTenantBranding";
import TenantHeader from "../TenantHeader";
import authService from "../../services/authService";
import QRCode from "react-qr-code";
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";

const Rapport = () => {
  const navigate = useNavigate();
  const { publicId } = useParams();
  const location = useLocation();

  const branding = useTenantBranding();
  const [examen, setExamen] = useState(null);
  const [rapport, setRapport] = useState(null);
  const [images, setImages] = useState([]);
  const [popupMessage, setPopupMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  const nomAuteur = rapport?.nomAuteur?.trim().toUpperCase();
const prenomAuteur = rapport?.prenomAuteur?.trim().toUpperCase();

const afficherSignatureAmoussou =    nomAuteur === "AMOUSSOU" && prenomAuteur === "KOMLAN";

  useEffect(() => {
    if (location.state?.rapport) {
      setRapport(location.state.rapport);
    }

    if (!publicId) return;

    api
      .get(`${process.env.REACT_APP_EUREKA_PACS_URL}/api/public/examen/${publicId}/images`)
      .then((res) => {
        setExamen(res.data);
        setImages(res.data.images || []);
      })
      .catch((err) => console.error("Erreur fetch examen/images :", err));
  }, [publicId, location.state]);

  if (!examen) {
    return <p>Chargement des détails de l'examen...</p>;
  }

  // === fonction terminer examen ===
  const handleTerminer = async () => {
    try {
      await api.put(
        `${process.env.REACT_APP_EUREKA_PACS_URL}/api/examen/${publicId}`,
        { statut: "TERMINE" },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      setPopupMessage("✅ L’examen a été terminé avec succès.");
      setShowPopup(true);
    } catch (error) {
      console.error("Erreur lors de la terminaison :", error);
      setPopupMessage("❌ Erreur lors de la terminaison de l’examen.");
      setShowPopup(true);
    }
  };

  const formatDateFr = (d) => {
    if (!d) return "";
    const date = new Date(d);
    return new Intl.DateTimeFormat("fr-FR", { dateStyle: "long" }).format(date);
  };

  const getVille = () => {
    return (
      examen?.etablissementVille ||
      examen?.ville ||
      branding?.city ||
      branding?.etablissement?.ville ||
      ""
    );
  };

  const openOHIFViewer = (examId) => {
    const url = process.env.REACT_APP_VIEWER_URL + `/viewer/${examId}`;
    const windowFeatures = "width=800,height=600,resizable,scrollbars=yes";
    const newWindow = window.open(url, "_blank", windowFeatures);
    if (newWindow) newWindow.focus();
    else alert("La fenêtre pop-up a été bloquée par votre navigateur.");
  };

  const calculerAge = (dateNaissance) => {
    if (!dateNaissance) return "—";
    const naissance = new Date(dateNaissance);
    const maintenant = new Date();
    let age = maintenant.getFullYear() - naissance.getFullYear();
    const mois = maintenant.getMonth() - naissance.getMonth();
    if (mois < 0 || (mois === 0 && maintenant.getDate() < naissance.getDate())) {
      age--;
    }
    return age + " ans";
  };

  return (
    <div className="report-container">
      <div className="buttons">
        <button onClick={() => navigate("/accueil")}>
          <span role="img" aria-label="acceuil">🏠</span>Retour à l’accueil
        </button>

        <button className="btn btn-blue" onClick={() => window.location.reload()}>
          <span role="img" aria-label="Actualiser">🔄</span>Actualiser
        </button>

        <button className="save-button">
          <Link to={`/examens/${publicId}`}>
            <span role="img" aria-label="Éditer">✏️</span> Retour examen
          </Link>
        </button>

        <button className="btn btn-green" onClick={() => window.print()}>
          <span role="img" aria-label="Générer">🖨️</span> Imprimer
        </button>

        {(authService.isRadiologue() || authService.isTechnicien()) && (
          <>
            <button
              className="btn btn-purple"
              onClick={() => openOHIFViewer(examen.dicom?.[0]?.study)}
            >
              <span role="img" aria-label="Visualiser">👁️</span> Visualiser les images
            </button>

            {/* ✅ nouveau bouton terminer */}
            <button className="btn btn-validate" onClick={handleTerminer}>
              ✅ Terminer l’examen
            </button>
          </>
        )}
      </div>

      <TenantHeader branding={branding} />

      <div className="title-block">
        <h2 className="title">{rapport?.titre || examen?.titre || "EXAMEN"}</h2>
        <div className="fait-a">
          Fait {getVille() ? `à ${getVille()}, ` : ""}le {formatDateFr(rapport?.createdAt)}
        </div>
      </div>

      {/* Infos patient */}
      <div className="patient-info">
        <div className="row">
          <span className="label">NOM :</span>
          <span className="line">{examen.patientNom || ""}</span>

          <span className="label">PRENOMS :</span>
          <span className="line">{examen.patientPrenom || ""}</span>
        </div>

        <div className="row">
          <span className="label">AGE :</span>
          <span className="line">
            {examen.dateNaissance ? calculerAge(examen.dateNaissance) : "—"}
          </span>

          <span className="label">SEXE :</span>
          <span className="line">{examen.patientSexe || ""}</span>
        </div>

        {(rapport?.indication || examen.indication) && (
          <div className="row">
            <span className="label">INDICATION :</span>
            <span className="line line-full">
              {rapport?.indication || examen.indication}
            </span>
          </div>
        )}
      </div>

      {(rapport?.resultat || examen.resultat) && (
        <>
          <h3 className="section-caption">RESULTAT</h3>
          <div
            className="rich-text ql-editor"
            dangerouslySetInnerHTML={{
              __html: rapport?.resultat || examen.resultat,
            }}
          />
        </>
      )}

      {(rapport?.conclusion || examen.conclusion) && (
        <section className="conclusion-block">
          <h3 className="section-caption">CONCLUSION</h3>
          <div
            className="rich-text ql-editor"
            dangerouslySetInnerHTML={{
              __html: rapport?.conclusion || examen.conclusion,
            }}
          />

          <div className="footer-row">
            {images.length > 0 && (
              <div className="qr-left">
                <QRCode
                  value={`https://eurekapacs.app/public/viewer/${publicId}`}
                  size={120}
                />
                <div className="qr-caption">
                  <em>Scanner ce QR pour accéder<br />aux images de l'examen</em>
                </div>
              </div>
            )}

            <div className="signature">
              <em>Médecin Radiologue,</em><br />
              <strong>
                Dr {rapport?.nomAuteur?.toUpperCase() || "________"}{" "}
                {rapport?.prenomAuteur || ""}.
              </strong>

              {afficherSignatureAmoussou && (
                <div style={{ marginTop: "8px" }}>
                  <img
                    src="/signature/signatureDrAmoussou.png"
                    alt="Signature Dr Amoussou"
                    style={{
                      width: "180px",
                      height: "auto",
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Popup info */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <p>{popupMessage}</p>
            <button className="btn btn-blue" onClick={() => setShowPopup(false)}>
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rapport;
